import { Box } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import {
  LwButton,
  MultiDateCalendar,
  StyledActionsBox,
  StyledContentBox,
  StyledDivider,
  StyledForm,
  StyledTitle,
} from 'redesign';
import { useCalendarDefaults } from 'shared/hooks/useCalendarDefaults';
import { PartnerCheckoutInboxFiltersResponse } from 'partner/shared/services/partner-checkouts-service.types';
import { useCallback, useMemo } from 'react';
import { FilterProps, InboxFiltersParams, InboxFormValues } from './inbox-filter-form.types';
import { BusinessUnitFiltering } from './business-unit-filtering';
import { useCalendar } from '../../../../shared/hooks/use-calendar';

export const InboxFilterForm = ({
  activeFilters,
  applyFilters,
  onCancel,
  clear,
  data,
}: FilterProps<InboxFiltersParams>) => {
  const { monthLabels, dayLabels } = useCalendarDefaults();
  const maxDate = useMemo(() => new Date(), []);

  const { dateRange, handleDateChange, getDateRangeFrom, handleResetDateRange } = useCalendar({
    fromDate: activeFilters.fromDate,
    toDate: activeFilters.toDate,
  });
  const form = useForm<InboxFormValues>({
    defaultValues: {
      ...activeFilters,
      ...getBranchOfficeDefaultValues(data, activeFilters),
      jobs: activeFilters.jobs?.map((jobId) => {
        const job = data.jobs.find((j) => j.id === jobId);
        return { name: job?.name ?? '', id: jobId };
      }),
      companies: activeFilters.companies?.map((companyId) => {
        const company = data.companies.find((c) => c.id === companyId);
        return { name: company?.name ?? '', id: companyId };
      }),
    },
  });

  const onSubmit = useCallback(
    (formData: InboxFormValues) => {
      const submitData: InboxFiltersParams = {
        companies: formData.companies?.map((company) => company.id),
        departments: formData.departments?.map((department) => department.id),
        branchOffices: formData.branchOffices?.map((branchOffice) => branchOffice.id),
        jobs: formData.jobs?.map((job) => job.id),
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      };

      applyFilters(submitData);
      onCancel();
    },
    [applyFilters, dateRange.fromDate, dateRange.toDate, onCancel]
  );

  const clearForm = useCallback(() => {
    clear();
    form.reset({
      companies: [],
      jobs: [],
      departments: [],
      branchOffices: [],
    });
    handleResetDateRange();
  }, [clear, form, handleResetDateRange]);

  return (
    <FormProvider {...form}>
      <StyledContentBox>
        <StyledTitle variant="h2">Filter Checkouts</StyledTitle>
        <StyledDivider />
        <StyledForm onSubmit={form.handleSubmit(onSubmit)}>
          <Box>
            <MultiDateCalendar
              weekStartDayIndex={1}
              range
              months={monthLabels}
              weekDays={dayLabels}
              value={getDateRangeFrom(dateRange.fromDate, dateRange.toDate)}
              maxDate={maxDate}
              onChange={handleDateChange}
            />
            <Box flex="1" justifyContent="flex-end" marginBottom={6}>
              <LwButton color="secondary" onClick={handleResetDateRange}>
                Datumselectie wissen
              </LwButton>
            </Box>
            <BusinessUnitFiltering
              {...{ applyFilters, onCancel, clear }}
              companies={data.companies}
              jobs={data.jobs}
            />
          </Box>
          <StyledActionsBox>
            <Box flexBasis="100%">
              <LwButton color="primary" type="submit">
                Filter
              </LwButton>
            </Box>
            <LwButton color="secondary" onClick={onCancel}>
              Terug
            </LwButton>
            <LwButton color="secondary" onClick={clearForm}>
              Filters wissen
            </LwButton>
          </StyledActionsBox>
        </StyledForm>
      </StyledContentBox>
    </FormProvider>
  );
};

const getBranchOfficeDefaultValues = (
  data: PartnerCheckoutInboxFiltersResponse,
  activeFilters: InboxFiltersParams
) => {
  const branchOfficesWithDepartments = data.companies
    .flatMap((item) => item.branchOffices)
    .filter((branchOffice) => activeFilters.branchOffices?.includes(branchOffice.id));
  const branchOffices = branchOfficesWithDepartments.map((item) => {
    return { name: item.name, id: item.id };
  });
  const departments = branchOfficesWithDepartments
    .flatMap((item) => item.departments)
    .filter((department) => activeFilters.departments?.includes(department.id))
    .map((item) => {
      return { name: item.name, id: item.id };
    });
  return { departments, branchOffices };
};
